import { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/auth-context';
import { DashboardLayout } from '@/components/layout/dashboard-layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';
import { Skeleton } from '@/components/ui/skeleton';
import { Settings, Key, CreditCard, Video, Save, Eye, EyeOff, Shield, CheckCircle, XCircle, Loader2, ChevronDown, ChevronUp, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';

interface AppSetting {
  id: string;
  setting_key: string;
  setting_value: string;
  description: string;
  is_sensitive: boolean;
}

export default function SettingsPage() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<Record<string, string>>({});
  const [showSecrets, setShowSecrets] = useState<Record<string, boolean>>({});
  const [testingPayPal, setTestingPayPal] = useState(false);
  const [testingTwilio, setTestingTwilio] = useState(false);
  const [paypalTestResult, setPaypalTestResult] = useState<{ success: boolean; message: string; details?: any } | null>(null);
  const [twilioTestResult, setTwilioTestResult] = useState<{ success: boolean; message: string; details?: any } | null>(null);
  const [showPaypalDetails, setShowPaypalDetails] = useState(false);
  const [showTwilioDetails, setShowTwilioDetails] = useState(false);

  useEffect(() => {
    if (profile) {
      if (profile.role !== 'admin') {
        navigate('/dashboard');
        return;
      }
      fetchSettings();
    }
  }, [profile]);

  const fetchSettings = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('app_settings')
      .select('*')
      .order('setting_key');

    if (error) {
      console.error('Error fetching settings:', error);
      toast({
        title: 'Errore',
        description: 'Impossibile caricare le impostazioni',
        variant: 'destructive',
      });
    } else if (data) {
      const settingsMap: Record<string, string> = {};
      data.forEach((setting: AppSetting) => {
        settingsMap[setting.setting_key] = setting.setting_value || '';
      });
      setSettings(settingsMap);
    }
    setLoading(false);
  };

  const handleSettingChange = (key: string, value: string) => {
    setSettings(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  const toggleShowSecret = (key: string) => {
    setShowSecrets(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleSaveSettings = async () => {
    setSaving(true);

    try {
      const updates = Object.entries(settings).map(([key, value]) =>
        supabase
          .from('app_settings')
          .update({
            setting_value: value,
            updated_at: new Date().toISOString(),
            updated_by: profile?.id,
          })
          .eq('setting_key', key)
      );

      const results = await Promise.all(updates);
      const hasError = results.some(result => result.error);

      if (hasError) {
        throw new Error('Failed to update some settings');
      }

      toast({
        title: 'Impostazioni salvate',
        description: 'Le impostazioni sono state aggiornate con successo',
      });

      setPaypalTestResult(null);
      setTwilioTestResult(null);
    } catch (error) {
      console.error('Error saving settings:', error);
      toast({
        title: 'Errore',
        description: 'Impossibile salvare le impostazioni',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleTestPayPal = async () => {
    setTestingPayPal(true);
    setPaypalTestResult(null);

    try {
      const response = await fetch('/api/test/paypal', {
        method: 'POST',
      });

      const data = await response.json();

      setPaypalTestResult({
        success: data.success,
        message: data.message || data.error,
        details: data.details,
      });

      if (data.success) {
        toast({
          title: 'Test PayPal riuscito',
          description: data.message,
        });
      } else {
        toast({
          title: 'Test PayPal fallito',
          description: data.message,
          variant: 'destructive',
        });
      }
    } catch (error) {
      setPaypalTestResult({
        success: false,
        message: 'Errore durante il test della connessione',
      });
      toast({
        title: 'Errore',
        description: 'Impossibile testare la connessione PayPal',
        variant: 'destructive',
      });
    } finally {
      setTestingPayPal(false);
    }
  };

  const handleTestTwilio = async () => {
    setTestingTwilio(true);
    setTwilioTestResult(null);

    try {
      const response = await fetch('/api/test/twilio', {
        method: 'POST',
      });

      const data = await response.json();

      setTwilioTestResult({
        success: data.success,
        message: data.message || data.error,
        details: data.details,
      });

      if (data.success) {
        toast({
          title: 'Test Twilio riuscito',
          description: data.message,
        });
      } else {
        toast({
          title: 'Test Twilio fallito',
          description: data.message,
          variant: 'destructive',
        });
      }
    } catch (error) {
      setTwilioTestResult({
        success: false,
        message: 'Errore durante il test della connessione',
      });
      toast({
        title: 'Errore',
        description: 'Impossibile testare la connessione Twilio',
        variant: 'destructive',
      });
    } finally {
      setTestingTwilio(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <Skeleton className="h-12 w-64" />
          <Skeleton className="h-96" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Settings className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Impostazioni Applicazione</h1>
                <p className="text-gray-600 mt-1">Configura le chiavi API e le impostazioni del sistema</p>
              </div>
            </div>
          </div>
          <Badge variant="secondary" className="text-sm">
            <Shield className="w-4 h-4 mr-1" />
            Solo Admin
          </Badge>
        </div>

        <div className="grid gap-6 md:grid-cols-1 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-green-600" />
                <CardTitle>Configurazione PayPal</CardTitle>
              </div>
              <CardDescription>
                Chiavi API per l'integrazione dei pagamenti PayPal
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="paypal_client_id">Client ID</Label>
                <div className="relative">
                  <Input
                    id="paypal_client_id"
                    type={showSecrets['paypal_client_id'] ? 'text' : 'password'}
                    value={settings['paypal_client_id'] || ''}
                    onChange={(e) => handleSettingChange('paypal_client_id', e.target.value)}
                    placeholder="Inserisci il Client ID di PayPal"
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => toggleShowSecret('paypal_client_id')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showSecrets['paypal_client_id'] ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="paypal_client_secret">Client Secret</Label>
                <div className="relative">
                  <Input
                    id="paypal_client_secret"
                    type={showSecrets['paypal_client_secret'] ? 'text' : 'password'}
                    value={settings['paypal_client_secret'] || ''}
                    onChange={(e) => handleSettingChange('paypal_client_secret', e.target.value)}
                    placeholder="Inserisci il Client Secret di PayPal"
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => toggleShowSecret('paypal_client_secret')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showSecrets['paypal_client_secret'] ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="paypal_mode">Modalita</Label>
                <select
                  id="paypal_mode"
                  value={settings['paypal_mode'] || 'sandbox'}
                  onChange={(e) => handleSettingChange('paypal_mode', e.target.value)}
                  className="flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
                >
                  <option value="sandbox">Sandbox (Test)</option>
                  <option value="live">Live (Produzione)</option>
                </select>
              </div>

              <div className="pt-2">
                <p className="text-xs text-gray-500">
                  <Key className="w-3 h-3 inline mr-1" />
                  Ottieni le tue chiavi dal{' '}
                  <a
                    href="https://developer.paypal.com/dashboard/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    Dashboard PayPal Developer
                  </a>
                </p>
              </div>

              <Separator className="my-4" />

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-medium">Test Connessione</h4>
                </div>
                <Button
                  type="button"
                  onClick={handleTestPayPal}
                  disabled={testingPayPal || !settings['paypal_client_id'] || !settings['paypal_client_secret']}
                  variant="outline"
                  className="w-full"
                >
                  {testingPayPal && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Testa PayPal
                </Button>

                {paypalTestResult && (
                  <div className={`p-3 rounded-lg ${
                    paypalTestResult.success
                      ? 'bg-green-50 border border-green-200'
                      : 'bg-red-50 border border-red-200'
                  }`}>
                    <div className="flex items-start gap-2">
                      {paypalTestResult.success ? (
                        <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                      )}
                      <div className="flex-1">
                        <p className={`text-sm font-medium ${
                          paypalTestResult.success ? 'text-green-900' : 'text-red-900'
                        }`}>
                          {paypalTestResult.success ? 'Connessione riuscita' : 'Connessione fallita'}
                        </p>
                        <p className={`text-xs mt-1 ${
                          paypalTestResult.success ? 'text-green-700' : 'text-red-700'
                        }`}>
                          {paypalTestResult.message}
                        </p>
                      </div>
                    </div>

                    {paypalTestResult.details && !paypalTestResult.success && (
                      <Collapsible open={showPaypalDetails} onOpenChange={setShowPaypalDetails} className="mt-3">
                        <CollapsibleTrigger className="flex items-center gap-2 text-xs text-red-700 hover:text-red-800 font-medium">
                          <AlertCircle className="w-3 h-3" />
                          {showPaypalDetails ? 'Nascondi dettagli tecnici' : 'Mostra dettagli tecnici'}
                          {showPaypalDetails ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                        </CollapsibleTrigger>
                        <CollapsibleContent className="mt-2">
                          <div className="bg-red-100 p-2 rounded text-xs font-mono text-red-900 overflow-auto max-h-40">
                            <pre className="whitespace-pre-wrap break-words">
                              {JSON.stringify(paypalTestResult.details, null, 2)}
                            </pre>
                          </div>
                        </CollapsibleContent>
                      </Collapsible>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Video className="w-5 h-5 text-blue-600" />
                <CardTitle>Configurazione Twilio</CardTitle>
              </div>
              <CardDescription>
                Chiavi API per le videochiamate e le live session
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="twilio_account_sid">Account SID</Label>
                <div className="relative">
                  <Input
                    id="twilio_account_sid"
                    type={showSecrets['twilio_account_sid'] ? 'text' : 'password'}
                    value={settings['twilio_account_sid'] || ''}
                    onChange={(e) => handleSettingChange('twilio_account_sid', e.target.value)}
                    placeholder="Inserisci l'Account SID di Twilio"
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => toggleShowSecret('twilio_account_sid')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showSecrets['twilio_account_sid'] ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="twilio_api_key">API Key</Label>
                <div className="relative">
                  <Input
                    id="twilio_api_key"
                    type={showSecrets['twilio_api_key'] ? 'text' : 'password'}
                    value={settings['twilio_api_key'] || ''}
                    onChange={(e) => handleSettingChange('twilio_api_key', e.target.value)}
                    placeholder="Inserisci l'API Key di Twilio"
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => toggleShowSecret('twilio_api_key')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showSecrets['twilio_api_key'] ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="twilio_api_secret">API Secret</Label>
                <div className="relative">
                  <Input
                    id="twilio_api_secret"
                    type={showSecrets['twilio_api_secret'] ? 'text' : 'password'}
                    value={settings['twilio_api_secret'] || ''}
                    onChange={(e) => handleSettingChange('twilio_api_secret', e.target.value)}
                    placeholder="Inserisci l'API Secret di Twilio"
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => toggleShowSecret('twilio_api_secret')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showSecrets['twilio_api_secret'] ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>

              <div className="pt-2">
                <p className="text-xs text-gray-500">
                  <Key className="w-3 h-3 inline mr-1" />
                  Ottieni le tue chiavi dalla{' '}
                  <a
                    href="https://console.twilio.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    Console Twilio
                  </a>
                </p>
              </div>

              <Separator className="my-4" />

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-medium">Test Connessione</h4>
                </div>
                <Button
                  type="button"
                  onClick={handleTestTwilio}
                  disabled={testingTwilio || !settings['twilio_account_sid'] || !settings['twilio_api_key'] || !settings['twilio_api_secret']}
                  variant="outline"
                  className="w-full"
                >
                  {testingTwilio && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Testa Twilio
                </Button>

                {twilioTestResult && (
                  <div className={`p-3 rounded-lg ${
                    twilioTestResult.success
                      ? 'bg-green-50 border border-green-200'
                      : 'bg-red-50 border border-red-200'
                  }`}>
                    <div className="flex items-start gap-2">
                      {twilioTestResult.success ? (
                        <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                      )}
                      <div className="flex-1">
                        <p className={`text-sm font-medium ${
                          twilioTestResult.success ? 'text-green-900' : 'text-red-900'
                        }`}>
                          {twilioTestResult.success ? 'Connessione riuscita' : 'Connessione fallita'}
                        </p>
                        <p className={`text-xs mt-1 ${
                          twilioTestResult.success ? 'text-green-700' : 'text-red-700'
                        }`}>
                          {twilioTestResult.message}
                        </p>
                      </div>
                    </div>

                    {twilioTestResult.details && !twilioTestResult.success && (
                      <Collapsible open={showTwilioDetails} onOpenChange={setShowTwilioDetails} className="mt-3">
                        <CollapsibleTrigger className="flex items-center gap-2 text-xs text-red-700 hover:text-red-800 font-medium">
                          <AlertCircle className="w-3 h-3" />
                          {showTwilioDetails ? 'Nascondi dettagli tecnici' : 'Mostra dettagli tecnici'}
                          {showTwilioDetails ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                        </CollapsibleTrigger>
                        <CollapsibleContent className="mt-2">
                          <div className="bg-red-100 p-2 rounded text-xs font-mono text-red-900 overflow-auto max-h-40">
                            <pre className="whitespace-pre-wrap break-words">
                              {JSON.stringify(twilioTestResult.details, null, 2)}
                            </pre>
                          </div>
                        </CollapsibleContent>
                      </Collapsible>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        <Card className="border-2 border-blue-200 bg-blue-50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Shield className="w-6 h-6 text-blue-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-2">Nota sulla sicurezza</h3>
                <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
                  <li>Le chiavi API sono memorizzate in modo sicuro nel database</li>
                  <li>Solo gli amministratori possono visualizzare e modificare queste impostazioni</li>
                  <li>Non condividere mai le tue chiavi API con nessuno</li>
                  <li>Usa la modalita Sandbox per i test prima di passare alla produzione</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end">
          <Button
            onClick={handleSaveSettings}
            disabled={saving}
            size="lg"
            className="min-w-[200px]"
          >
            <Save className="w-5 h-5 mr-2" />
            {saving ? 'Salvataggio...' : 'Salva Impostazioni'}
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
}
